/**
 * GRN Allocation Repair — superuser drag-and-drop tool.
 *
 * Lists Type-4 PO lines whose GRNs were left on the synthetic line-0 anchor
 * (or any user-requested filter), and lets a superuser drop GRN cards onto
 * the correct schedule. Each drop fires the existing ``link-schedule``
 * action, which atomically:
 *   - rewrites the GRNScheduleAllocation row
 *   - updates the GRN's primary po_line_schedule FK
 *   - recomputes on_time_status via the supplier's OTIF window
 *   - reconciles shipped_qty / balance_qty / status on the affected schedules
 *   - invalidates the company dashboard cache + supplier performance cache
 *
 * Gate: a Scheduled-enabled install, plus ``is_superuser`` or ``is_staff``.
 * The schedule condition binds superusers too — with no schedule lines there
 * are no allocations to repair. Hidden from the sidebar under the same rule.
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  DndContext, DragOverlay, PointerSensor,
  useSensor, useSensors, type DragEndEvent, type DragStartEvent,
} from '@dnd-kit/core';
import { Loader2, AlertCircle, RefreshCw, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';
import {
  grnRepairAPI,
  type RepairPoLineRow,
  type RepairPoLineDetail,
  type RepairGRN,
  type RepairFilterMode,
} from '../../lib/api/grn-repair';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { GrnCard, DraggableGrn, ScheduleDropZone, fmtDate } from './GrnRepairCards';
import { SplitModal, CancelScheduleModal, UncancelScheduleModal, QtyEditModal } from './GrnRepairModals';
import { GrnRepairFilters } from './GrnRepairFilters';
import { useGrnScheduleEdits } from './useGrnScheduleEdits';

export default function GrnAllocationRepair() {
  const { user } = useAuth();
  const { isDashboardTabEnabled } = useMenuSettings();
  // Superusers always; Django staff on Scheduled-enabled installs. Mirrors the
  // backend ``_require_repair_access`` gate.
  const canRepair = isDashboardTabEnabled('scheduled')
    && (!!user?.is_superuser || !!user?.is_staff);
  const [rows, setRows] = useState<RepairPoLineRow[]>([]);
  const [count, setCount] = useState(0);
  const [scheduleCount, setScheduleCount] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [poSearch, setPoSearch] = useState('');
  const [partSearch, setPartSearch] = useState('');
  const [supplierSearch, setSupplierSearch] = useState('');
  const [filterMode, setFilterMode] = useState<RepairFilterMode>('line0');
  const [listLoading, setListLoading] = useState(false);
  const [selectedLineId, setSelectedLineId] = useState<number | null>(null);
  const [detail, setDetail] = useState<RepairPoLineDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeDragGrn, setActiveDragGrn] = useState<RepairGRN | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const fetchList = useCallback(() => {
    setListLoading(true);
    // Derive this week's Monday from the browser clock — identical to the
    // Schedules tabs' getThisWeekRange — so the date buckets share one week
    // boundary and the counts reconcile (no server-vs-browser midnight skew).
    const today = new Date();
    const daysSinceMonday = (today.getDay() + 6) % 7;
    const monday = new Date(today);
    monday.setDate(today.getDate() - daysSinceMonday);
    monday.setHours(0, 0, 0, 0);
    const weekStart = `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;
    grnRepairAPI.listPoLines({
      po_search: poSearch || undefined,
      part_search: partSearch || undefined,
      supplier_search: supplierSearch || undefined,
      filter_mode: filterMode,
      week_start: weekStart,
      page,
      page_size: 50,
    })
      .then((resp) => {
        setRows(resp.data.results);
        setCount(resp.data.count);
        setScheduleCount(resp.data.schedule_count);
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load PO lines'))
      .finally(() => setListLoading(false));
  }, [poSearch, partSearch, supplierSearch, filterMode, page]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const fetchDetail = useCallback((id: number) => {
    setDetailLoading(true);
    setError(null);
    grnRepairAPI.getPoLineDetail(id)
      .then((resp) => setDetail(resp.data))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load PO line detail'))
      .finally(() => setDetailLoading(false));
  }, []);

  // Per-schedule allocation slices. One render entry per
  // GRNScheduleAllocation row, so an over-delivery GRN appears under each
  // schedule it spilled into with the *allocated* qty (not the GRN's full
  // received qty). GRNs with no allocation row + no primary FK go under the
  // "unassigned" pseudo-bucket below.
  // Each slot is a draggable card. Allocation-backed slots carry an
  // ``allocationId`` so per-allocation API calls (move / split) operate on
  // a specific row. Legacy slots (GRNs with no allocation rows) fall back
  // to the whole-GRN ``link-schedule`` endpoint.
  type Slot = {
    key: string;            // also the dnd-kit drag id
    grn: RepairGRN;
    qty: string;
    isOverDelivery: boolean;
    allocationId: number | null;
  };
  const slotsBySched = useMemo(() => {
    const m = new Map<number | string, Slot[]>();
    if (!detail) return m;
    const grnById = new Map(detail.grns.map((g) => [g.id, g] as const));
    const grnsWithAlloc = new Set<number>();
    for (const a of detail.allocations) {
      const g = grnById.get(a.grn_id);
      if (!g) continue;
      grnsWithAlloc.add(g.id);
      const arr = m.get(a.po_line_schedule_id) || [];
      arr.push({
        key: `alloc-${a.id}`,
        grn: g,
        qty: a.allocated_qty,
        isOverDelivery: a.is_over_delivery,
        allocationId: a.id,
      });
      m.set(a.po_line_schedule_id, arr);
    }
    for (const g of detail.grns) {
      if (grnsWithAlloc.has(g.id)) continue;
      const k = g.po_line_schedule_id ?? 'unassigned';
      const arr = m.get(k) || [];
      arr.push({
        key: `grn-${g.id}`,
        grn: g,
        qty: g.quantity_received,
        isOverDelivery: false,
        allocationId: null,
      });
      m.set(k, arr);
    }
    return m;
  }, [detail]);

  const slotByKey = useMemo(() => {
    const m = new Map<string, Slot>();
    slotsBySched.forEach((arr) => arr.forEach((s) => m.set(s.key, s)));
    return m;
  }, [slotsBySched]);

  const onDragStart = (e: DragStartEvent) => {
    if (!detail) return;
    const slot = slotByKey.get(String(e.active.id));
    setActiveDragGrn(slot?.grn ?? null);
  };

  const [rebalancing, setRebalancing] = useState<null | 'closest'>(null);
  const performRebalance = useCallback(async () => {
    if (!detail) return;
    setRebalancing('closest');
    try {
      const resp = await grnRepairAPI.rebalance(detail.po_line.id);
      toast.success(`${resp.data.moved} GRN(s) rebalanced (closest-match).`);
      fetchDetail(detail.po_line.id);
      fetchList();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Rebalance failed');
    } finally {
      setRebalancing(null);
    }
  }, [detail, fetchDetail, fetchList]);

  const performSlotMove = useCallback(async (slot: Slot, schedId: number) => {
    if (!detail) return;
    const targetSched = detail.schedules.find((s) => s.id === schedId);
    if (!targetSched) return;
    const dest = `line ${targetSched.schedule_line_number}${targetSched.schedule_date ? ` (${fmtDate(targetSched.schedule_date)})` : ' (line 0)'}`;
    try {
      if (slot.allocationId !== null) {
        await grnRepairAPI.moveAllocation(slot.allocationId, schedId);
        toast.success(`GRN #${slot.grn.id} (${slot.qty}) → ${dest}`);
      } else {
        await grnRepairAPI.moveGrn(slot.grn.id, schedId);
        toast.success(`GRN #${slot.grn.id} → ${dest}`);
      }
      fetchDetail(detail.po_line.id);
      fetchList();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to move');
    }
  }, [detail, fetchDetail, fetchList]);

  const performDelete = useCallback(async (slot: Slot) => {
    if (!detail || slot.allocationId === null) return;
    try {
      await grnRepairAPI.deleteAllocation(slot.allocationId);
      toast.success(`GRN #${slot.grn.id} allocation (${slot.qty}) removed`);
      fetchDetail(detail.po_line.id);
      fetchList();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Delete failed');
    }
  }, [detail, fetchDetail, fetchList]);

  const [splitTarget, setSplitTarget] = useState<Slot | null>(null);
  const [splitQty, setSplitQty] = useState('');
  const performSplit = useCallback(async () => {
    if (!detail || !splitTarget || splitTarget.allocationId === null) return;
    const qty = splitQty.trim();
    if (!qty) { toast.error('Enter a split quantity'); return; }
    try {
      await grnRepairAPI.splitAllocation(splitTarget.allocationId, qty);
      toast.success(`GRN #${splitTarget.grn.id} split: ${qty} chipped off`);
      setSplitTarget(null);
      setSplitQty('');
      fetchDetail(detail.po_line.id);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Split failed');
    }
  }, [detail, splitTarget, splitQty, fetchDetail]);

  // Schedule-level edits (cancel line, edit qty) live in a hook to keep this
  // page under the 500-line ceiling.
  const refreshOpenLine = useCallback(() => {
    if (!detail) return;
    fetchDetail(detail.po_line.id);
    fetchList();
  }, [detail, fetchDetail, fetchList]);
  const {
    cancelTarget, setCancelTarget, cancelling, performCancelSchedule,
    uncancelTarget, setUncancelTarget, uncancelling, performUncancelSchedule,
    qtyTarget, setQtyTarget, savingQty, performSetQty,
  } = useGrnScheduleEdits(detail?.po_line.id ?? null, refreshOpenLine);

  const onDragEnd = async (e: DragEndEvent) => {
    setActiveDragGrn(null);
    if (!e.over || !detail) return;
    const slot = slotByKey.get(String(e.active.id));
    if (!slot) return;
    const schedId = Number(String(e.over.id).replace('sched-', ''));
    performSlotMove(slot, schedId);
  };

  const [recalculating, setRecalculating] = useState(false);
  const performRecalculate = useCallback(async () => {
    setRecalculating(true);
    try {
      const resp = await grnRepairAPI.recalculate();
      toast.success(resp.data.detail);
      // Give the background job a moment, then refresh the visible counts.
      setTimeout(() => { fetchList(); if (detail) fetchDetail(detail.po_line.id); }, 4000);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Recalculation failed to start');
    } finally {
      setRecalculating(false);
    }
  }, [fetchList, fetchDetail, detail]);

  if (!canRepair) {
    return (
      <div className="p-6">
        <Card className="p-6">
          <div className="flex gap-3 items-center text-red-600">
            <AlertCircle className="w-5 h-5" />
            <div>GRN Repair is only available on a Scheduled-enabled install, for superuser or staff accounts.</div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 flex flex-col gap-3 h-[calc(100vh-72px)]">
      {/* Top bar — title + global recalculate */}
      <div className="flex items-center justify-between flex-shrink-0">
        <div>
          <div className="text-sm font-semibold">GRN Allocation Repair</div>
          <div className="text-xs text-muted-foreground">Drag GRNs onto schedules; edit, cancel or recalc to settle counts &amp; OTIF.</div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={performRecalculate}
          disabled={recalculating}
          title="Reconcile shipped/balance, recompute active pointer + delivery dates, refresh OTIF and bust caches across all schedules. Does not re-point allocations."
        >
          {recalculating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
          Recalculate all
        </Button>
      </div>
      <div className="grid grid-cols-[340px_1fr] gap-4 flex-1 min-h-0">
      {/* LEFT — PO line list */}
      <Card className="p-3 flex flex-col overflow-hidden">
        <GrnRepairFilters
          filterMode={filterMode}
          onFilterMode={(m) => { setPage(1); setFilterMode(m); }}
          poSearch={poSearch}
          onPoSearch={(v) => { setPage(1); setPoSearch(v); }}
          partSearch={partSearch}
          onPartSearch={(v) => { setPage(1); setPartSearch(v); }}
          supplierSearch={supplierSearch}
          onSupplierSearch={(v) => { setPage(1); setSupplierSearch(v); }}
          onRefresh={fetchList}
          count={count}
          scheduleCount={scheduleCount}
        />
        <div className="flex-1 overflow-auto">
          {listLoading && <Loader2 className="w-4 h-4 animate-spin mx-auto my-4" />}
          {rows.map((r) => (
            <button
              key={r.po_line_id}
              onClick={() => { setSelectedLineId(r.po_line_id); fetchDetail(r.po_line_id); }}
              className={`block w-full text-left px-2 py-1.5 rounded text-sm hover:bg-slate-100 ${selectedLineId === r.po_line_id ? 'bg-blue-50' : ''}`}
            >
              <div className="font-mono">{r.po_number} / line {r.line_number}</div>
              <div className="text-xs text-muted-foreground">
                {r.supplier_name} • {r.bad_grn_count}{' '}
                {filterMode === 'line0' || filterMode === 'all' ? 'GRN(s)' : 'open sched(s)'}
                {' • '}{fmtDate(r.last_receipt)}
              </div>
            </button>
          ))}
        </div>
        {count > 50 && (
          <div className="flex gap-2 justify-center mt-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
            <span className="text-xs self-center">Page {page}</span>
            <Button variant="outline" size="sm" disabled={page * 50 >= count} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        )}
      </Card>

      {/* RIGHT — drag board */}
      <Card className="p-4 overflow-auto">
        {!detail && !detailLoading && (
          <div className="text-muted-foreground text-sm">Pick a PO line on the left.</div>
        )}
        {detailLoading && <Loader2 className="w-4 h-4 animate-spin" />}
        {error && (
          <div className="flex gap-2 text-red-600 text-sm mb-2"><AlertCircle className="w-4 h-4" />{error}</div>
        )}
        {detail && (
          <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
            <div className="mb-3 flex justify-between items-start gap-3">
              <div>
                <div className="text-lg font-semibold">
                  {detail.po_line.po_number} / line {detail.po_line.line_number}
                </div>
                <div className="text-xs text-muted-foreground">
                  {detail.po_line.supplier_name} • part {detail.po_line.part_number} • OTIF window: {detail.otif_window.days_early}d early / {detail.otif_window.days_late}d late
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={performRebalance}
                  disabled={rebalancing !== null || detailLoading}
                  title="Walk GRNs in receipt order; land each whole onto its closest eligible schedule (1d early → 5d late). Never splits. Line-0 receipts untouched."
                >
                  {rebalancing === 'closest' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                  Closest-match
                </Button>
                {/* FIFO + split is hidden from the toolbar. It fills schedules
                    earliest-first and SPLITS a receipt across several of them,
                    which contradicts how a delivery actually arrives — one
                    batch, one slot — and leaves part-quantities on slots no
                    single receipt ever satisfied. Closest-match is the
                    behaviour we want operators reaching for. The backend
                    endpoint (`repair-po-line-rebalance-fifo`) is deliberately
                    left in place — still callable for a deliberate one-off, and
                    removing it is a bigger decision than taking the button off
                    the toolbar. */}
              </div>
            </div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-2">
              <CheckCircle2 className="w-3 h-3" /> Drag GRN cards between schedule rows. Each drop saves immediately.
            </div>
            {detail.schedules.map((s) => (
              <ScheduleDropZone key={s.id} sched={s} onCancel={() => setCancelTarget(s)} onUncancel={() => setUncancelTarget(s)} onEditQty={() => setQtyTarget(s)}>
                {(slotsBySched.get(s.id) || []).map((slot) => (
                  <DraggableGrn
                    key={slot.key}
                    dragId={slot.key}
                    grn={slot.grn}
                    qtyOnSlot={slot.qty}
                    isOverDelivery={slot.isOverDelivery}
                    schedDate={s.schedule_date}
                    win={detail.otif_window}
                    schedules={detail.schedules}
                    onMove={(sid) => performSlotMove(slot, sid)}
                    onSplit={slot.allocationId !== null ? () => { setSplitTarget(slot); setSplitQty(''); } : undefined}
                    onDelete={slot.allocationId !== null ? () => performDelete(slot) : undefined}
                    canSplit={slot.allocationId !== null && parseFloat(slot.qty) > 0}
                    currentSchedId={s.id}
                  />
                ))}
              </ScheduleDropZone>
            ))}
            {(slotsBySched.get('unassigned') || []).length > 0 && (
              <div className="border-dashed border rounded p-3 mt-3">
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Unallocated (no schedule FK)</div>
                <div className="flex flex-wrap gap-2">
                  {(slotsBySched.get('unassigned') || []).map((slot) => (
                    <DraggableGrn
                      key={slot.key}
                      dragId={slot.key}
                      grn={slot.grn}
                      qtyOnSlot={slot.qty}
                      isOverDelivery={slot.isOverDelivery}
                      schedDate={null}
                      win={detail.otif_window}
                      schedules={detail.schedules}
                      onMove={(sid) => performSlotMove(slot, sid)}
                      onSplit={slot.allocationId !== null ? () => { setSplitTarget(slot); setSplitQty(''); } : undefined}
                      onDelete={slot.allocationId !== null ? () => performDelete(slot) : undefined}
                      canSplit={slot.allocationId !== null && parseFloat(slot.qty) > 0}
                      currentSchedId={null}
                    />
                  ))}
                </div>
              </div>
            )}
            <DragOverlay>
              {activeDragGrn ? <GrnCard grn={activeDragGrn} schedDate={null} win={detail.otif_window} /> : null}
            </DragOverlay>
          </DndContext>
        )}
      </Card>
      </div>

      {/* Split modal */}
      {splitTarget && (
        <SplitModal
          grnId={splitTarget.grn.id}
          currentQty={splitTarget.qty}
          splitQty={splitQty}
          onQtyChange={setSplitQty}
          onClose={() => setSplitTarget(null)}
          onSplit={performSplit}
        />
      )}

      {/* Cancel-schedule confirm modal */}
      {cancelTarget && (
        <CancelScheduleModal
          lineNumber={cancelTarget.schedule_line_number}
          schedDate={cancelTarget.schedule_date}
          cancelling={cancelling}
          onClose={() => setCancelTarget(null)}
          onConfirm={() => detail && performCancelSchedule(detail.schedules)}
        />
      )}

      {/* Un-cancel (reactivate) confirm modal */}
      {uncancelTarget && (
        <UncancelScheduleModal
          lineNumber={uncancelTarget.schedule_line_number}
          schedDate={uncancelTarget.schedule_date}
          uncancelling={uncancelling}
          onClose={() => setUncancelTarget(null)}
          onConfirm={performUncancelSchedule}
        />
      )}

      {/* Edit-quantity modal */}
      {qtyTarget && (
        <QtyEditModal
          lineNumber={qtyTarget.schedule_line_number}
          schedDate={qtyTarget.schedule_date}
          currentQty={qtyTarget.schedule_qty}
          saving={savingQty}
          onClose={() => setQtyTarget(null)}
          onSave={performSetQty}
        />
      )}
    </div>
  );
}
